/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GenAILiveClient } from "../lib/genai-live-client";
import { LiveClientOptions } from "../types";
import { AudioStreamer } from "../lib/audio-streamer";
import { audioContext } from "../lib/utils";
import VolMeterWorket from "../lib/worklets/vol-meter";
import { LiveConnectConfig, Modality } from "@google/genai";
import { useTranscriptStore } from "../lib/store-transcript";

export type UseLiveAPIResults = {
  client: GenAILiveClient;
  setConfig: (config: LiveConnectConfig | ((prev: LiveConnectConfig) => LiveConnectConfig)) => void;
  config: LiveConnectConfig;
  model: string;
  connected: boolean;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  volume: number;
  userVolume: number;
  setUserVolume: (volume: number) => void;
  isAiSpeaking: boolean;
  isThinking: boolean;
  setIsThinking: (thinking: boolean) => void;
  avatarMode: "meeting" | "door2door";
  setAvatarMode: (mode: "meeting" | "door2door") => void;
  audioStreamer: AudioStreamer | null;
};

export function useLiveAPI(options: LiveClientOptions): UseLiveAPIResults {
  const client = useMemo(() => new GenAILiveClient(options), [options]);
  const audioStreamerRef = useRef<AudioStreamer | null>(null);

  const [model] = useState<string>("gemini-3.1-flash-live-preview");
  const [config, setConfig] = useState<LiveConnectConfig>({
    responseModalities: [Modality.AUDIO],
    speechConfig: {
      voiceConfig: {
        prebuiltVoiceConfig: {
          voiceName: "Puck",
        },
      },
    },
    outputAudioTranscription: {},
    inputAudioTranscription: {},
  });
  const [connected, setConnected] = useState(false);
  const [volume, setVolume] = useState(0);
  const [userVolume, setUserVolume] = useState(0);
  const [isAiSpeaking, setIsAiSpeaking] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [avatarMode, setAvatarMode] = useState<"meeting" | "door2door">("meeting");
  const thinkingTimeoutRef = useRef<number | null>(null);
  const [audioStreamer, setAudioStreamer] = useState<AudioStreamer | null>(null);

  // register audio for streaming server -> speakers
  useEffect(() => {
    if (!audioStreamerRef.current) {
      audioContext({ id: "audio-out" }).then((audioCtx: AudioContext) => {
        audioStreamerRef.current = new AudioStreamer(audioCtx);
        audioStreamerRef.current
          .addWorklet<any>("vumeter-out", VolMeterWorket, (ev: any) => {
            setVolume(ev.data.volume);
          })
          .then(() => {
            // Successfully added worklet
            setAudioStreamer(audioStreamerRef.current);
          });
      });
    }
  }, [audioStreamerRef]);

  useEffect(() => {
    const onOpen = () => {
      setConnected(true);
    };

    const onClose = () => {
      setConnected(false);
    };

    const onError = (error: ErrorEvent) => {
      console.error("error", error);
    };

    const stopAudioStreamer = () => {
      audioStreamerRef.current?.stop();
      setIsAiSpeaking(false);
    };

    const onAudio = (data: ArrayBuffer) => {
      audioStreamerRef.current?.addPCM16(new Uint8Array(data));
      setIsAiSpeaking(true);
      setIsThinking(false);
      if (thinkingTimeoutRef.current) {
        clearTimeout(thinkingTimeoutRef.current);
        thinkingTimeoutRef.current = null;
      }
    };

    const onTurnComplete = () => {
      setIsAiSpeaking(false);
      setIsThinking(false);
    };

    const onToolCall = () => {
      setIsThinking(false);
    };

    const onAiTranscript = (text: string) => {
      useTranscriptStore.getState().updateLastTranscript("ai", text);
    };

    const onUserTranscript = (text: string) => {
      useTranscriptStore.getState().updateLastTranscript("user", text);
    };

    client
      .on("error", onError)
      .on("open", onOpen)
      .on("close", onClose)
      .on("interrupted", stopAudioStreamer)
      .on("audio", onAudio)
      .on("turncomplete", onTurnComplete)
      .on("toolcall", onToolCall)
      .on("aitranscript", onAiTranscript)
      .on("usertranscript", onUserTranscript);

    return () => {
      client
        .off("error", onError)
        .off("open", onOpen)
        .off("close", onClose)
        .off("interrupted", stopAudioStreamer)
        .off("audio", onAudio)
        .off("turncomplete", onTurnComplete)
        .off("aitranscript", onAiTranscript)
        .off("usertranscript", onUserTranscript)
        .disconnect();
    };
  }, [client]);

  const connect = useCallback(async () => {
    if (!config) {
      throw new Error("config has not been set");
    }
    await client.connect(model, config);
  }, [client, config, model]);

  const disconnect = useCallback(async () => {
    client.disconnect();
    audioStreamerRef.current?.stop();
    setConnected(false);
  }, [setConnected, client]);

  return {
    client,
    config,
    setConfig,
    model,
    connected,
    connect,
    disconnect,
    volume,
    userVolume,
    setUserVolume,
    isAiSpeaking,
    isThinking,
    setIsThinking,
    avatarMode,
    setAvatarMode,
    audioStreamer,
  };
}
